import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/services/api";
import type {
  InvoiceValidationResponse,
  ExceptionQueueParams,
  ExceptionQueueResponse,
  ValidationLogsResponse,
  CreateValidationLogEntry,
  CreateValidationLogsResponse,
} from "../types/action";
import type { GrnMappingResponse, GrnAvailabilityResponse } from "../types/invoice";
import { invoiceKeys, INVOICE_BASE_URL } from "./queryKeys";
import { toQueryString } from "@/helpers/queryString";

// --- getValidation ---

export async function getValidation(id: number): Promise<InvoiceValidationResponse> {
  return api.get(`${INVOICE_BASE_URL}/${id}/validation`);
}

export function invoiceValidationQuery(id: number) {
  return {
    queryKey: invoiceKeys.validation(id),
    queryFn: () => getValidation(id),
    enabled: !!id,
  };
}

export function useInvoiceValidation(id: number) {
  return useQuery(invoiceValidationQuery(id));
}

// --- getValidationLogs ---

export async function getValidationLogs(id: number): Promise<ValidationLogsResponse> {
  return api.get(`${INVOICE_BASE_URL}/${id}/validation-logs`);
}

export function validationLogsQuery(id: number) {
  return {
    queryKey: invoiceKeys.validationLogs(id),
    queryFn: () => getValidationLogs(id),
    enabled: !!id,
  };
}

export function useValidationLogs(id: number) {
  return useQuery(validationLogsQuery(id));
}

// --- createValidationLogs ---

export async function createValidationLogs(
  id: number,
  logs: CreateValidationLogEntry[]
): Promise<CreateValidationLogsResponse> {
  return api.post(`${INVOICE_BASE_URL}/${id}/validation-logs`, { logs });
}

export function useCreateValidationLogs(invoiceId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (logs: CreateValidationLogEntry[]) => createValidationLogs(invoiceId, logs),
    onSuccess: () => {
      queryClient.invalidateQueries(invoiceKeys.validationLogs(invoiceId));
    },
  });
}

// --- getGrnMappings ---

export async function getGrnMappings(id: number): Promise<GrnMappingResponse> {
  return api.get(`${INVOICE_BASE_URL}/${id}/grn-mapping`);
}

export function grnMappingsQuery(id: number) {
  return {
    queryKey: invoiceKeys.grn(id),
    queryFn: () => getGrnMappings(id),
    enabled: !!id,
  };
}

export function useGrnMappings(id: number) {
  return useQuery(grnMappingsQuery(id));
}

// --- getAvailableGrns ---

const V1_VIP_BASE_URL = "/fulfilment/accounts-payable/api/v1/vip";

// po_code goes in the query, not the path: RCS PO codes contain "/" and the
// /fulfilment/* gateway decodes %2F before VIP routes the request (ENG-28753).
export async function getAvailableGrns(poCode: string): Promise<GrnAvailabilityResponse> {
  return api.get(`${V1_VIP_BASE_URL}/grn-list?po_code=${encodeURIComponent(poCode)}`);
}

// --- getExceptionQueue ---

export async function getExceptionQueue(params: ExceptionQueueParams): Promise<ExceptionQueueResponse> {
  const qs = toQueryString(params);
  return api.get(`${INVOICE_BASE_URL}/exceptions${qs ? `?${qs}` : ""}`);
}

export function exceptionQueueQuery(params: ExceptionQueueParams) {
  return {
    queryKey: invoiceKeys.exceptionList(params),
    queryFn: () => getExceptionQueue(params),
    keepPreviousData: true,
  };
}

export function useExceptionQueue(params: ExceptionQueueParams) {
  return useQuery(exceptionQueueQuery(params));
}
